import { useSyncExternalStore } from "react";
import type {
  Category,
  DeliveryMethodConfig,
  FreeShippingRule,
  HeroCampaign,
  Product,
} from "@/types";
import { products as defaultProducts } from "@/mocks/products";
import { categories as defaultCategories } from "@/mocks/categories";
import { heroCampaigns as defaultHeroCampaigns } from "@/mocks/heroCampaigns";
import { topBarMessages as defaultTopBarMessages } from "@/mocks/navigation";
import {
  deliveryMethods as defaultDeliveryMethods,
  freeShippingRules as defaultFreeShippingRules,
  storeConfig,
} from "@/config/storeConfig";
import { seoConfig as defaultSeo, storeSettings as defaultStoreSettings } from "@/mocks/panelContent";

/**
 * Camada central de conteúdo da Auréa.
 *
 * É a fonte única que o site público lê e que o painel administrativo edita.
 * As alterações são salvas no localStorage deste navegador (modo de teste) e
 * passam a valer imediatamente para o visitante por aqui, sem afetar ninguém.
 * O botão "Restaurar conteúdo original" remove todas as sobrescritas.
 */

export interface StoreSettingsContent {
  name: string;
  complement: string;
  slogan: string;
  phone: string;
  whatsapp: string;
  instagram: string;
  address: string;
  hours: string;
  institutionalText: string;
  privacyPolicy: string;
  exchangePolicy: string;
  currency: string;
  freeShippingValue: string;
}

export interface WhatsAppContent {
  number: string;
  display: string;
  defaultMessage: string;
  orderSignature: string;
  buttonEnabled: boolean;
  buttonText: string;
  origin: string;
}

export interface SeoContent {
  title: string;
  description: string;
  keywords: string;
  canonical: string;
  robots: string;
  favicon: string;
  ogImage: string;
  ogAlt: string;
}

export interface ImageOverride {
  url?: string;
  alt?: string;
  focal?: string;
}

export interface SiteContentState {
  products: Product[];
  categories: Category[];
  heroCampaigns: HeroCampaign[];
  topBarMessages: string[];
  delivery: DeliveryMethodConfig[];
  freeShipping: FreeShippingRule[];
  texts: Record<string, string>;
  images: Record<string, ImageOverride>;
  whatsapp: WhatsAppContent;
  settings: StoreSettingsContent;
  seo: SeoContent;
}

const STORAGE_KEY = "aurea-conteudo-v1";

/* ------------------------------------------------------------------ */
/* Valores padrão                                                      */
/* ------------------------------------------------------------------ */

const defaultWhatsApp: WhatsAppContent = {
  number: storeConfig.whatsappNumber,
  display: defaultStoreSettings.whatsapp,
  defaultMessage: "Olá! Vim pelo site da Auréa e gostaria de ajuda para escolher um calçado.",
  orderSignature: "Pedido iniciado pelo site Auréa — modelo demonstrativo da Vellora Studio.",
  buttonEnabled: true,
  buttonText: "Falar no WhatsApp",
  origin: storeConfig.origin,
};

const defaultSettings: StoreSettingsContent = {
  name: defaultStoreSettings.name,
  complement: defaultStoreSettings.complement,
  slogan: defaultStoreSettings.slogan,
  phone: defaultStoreSettings.phone,
  whatsapp: defaultStoreSettings.whatsapp,
  instagram: defaultStoreSettings.instagram,
  address: defaultStoreSettings.address,
  hours: defaultStoreSettings.hours,
  institutionalText: defaultStoreSettings.institutionalText,
  privacyPolicy: defaultStoreSettings.privacyPolicy,
  exchangePolicy: defaultStoreSettings.exchangePolicy,
  currency: "BRL (R$)",
  freeShippingValue: defaultStoreSettings.freeShippingValue,
};

const defaultSeoState: SeoContent = {
  title: defaultSeo.title,
  description: defaultSeo.description,
  keywords:
    "calçados femininos, sandálias, scarpins, sapatilhas, mules, mocassins, tênis feminino, bolsas, acessórios",
  canonical: defaultSeo.canonical,
  robots: defaultSeo.robots,
  favicon: defaultSeo.favicon,
  ogImage: defaultSeo.ogImage,
  ogAlt: "Seleção de calçados e acessórios da Auréa sobre fundo marfim",
};

const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T;

const buildDefaults = (): SiteContentState => ({
  products: clone(defaultProducts),
  categories: clone(defaultCategories),
  heroCampaigns: clone(defaultHeroCampaigns),
  topBarMessages: [...defaultTopBarMessages],
  delivery: clone(defaultDeliveryMethods),
  freeShipping: clone(defaultFreeShippingRules),
  texts: {},
  images: {},
  whatsapp: { ...defaultWhatsApp },
  settings: { ...defaultSettings },
  seo: { ...defaultSeoState },
});

/* ------------------------------------------------------------------ */
/* Motor de armazenamento                                              */
/* ------------------------------------------------------------------ */

function load(): SiteContentState {
  const base = buildDefaults();
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return base;
    const parsed = JSON.parse(raw) as Partial<SiteContentState>;
    return {
      products: parsed.products ?? base.products,
      categories: parsed.categories ?? base.categories,
      heroCampaigns: parsed.heroCampaigns ?? base.heroCampaigns,
      topBarMessages: parsed.topBarMessages ?? base.topBarMessages,
      delivery: parsed.delivery ?? base.delivery,
      freeShipping: parsed.freeShipping ?? base.freeShipping,
      texts: parsed.texts ?? {},
      images: parsed.images ?? {},
      whatsapp: { ...base.whatsapp, ...(parsed.whatsapp ?? {}) },
      settings: { ...base.settings, ...(parsed.settings ?? {}) },
      seo: { ...base.seo, ...(parsed.seo ?? {}) },
    };
  } catch {
    return base;
  }
}

let state: SiteContentState = load();
let previousState: SiteContentState | null = null;
const listeners = new Set<() => void>();

function emit(): void {
  listeners.forEach((listener) => listener());
}

function persist(): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* armazenamento indisponível */
  }
}

function commit(patch: Partial<SiteContentState>): void {
  previousState = state;
  state = { ...state, ...patch };
  persist();
  emit();
}

/** Estado do conteúdo imediatamente ANTES da última alteração (para desfazer). */
export const getPreviousContent = (): SiteContentState | null => previousState;

/** Reaplica um estado completo de conteúdo (usado para restaurar uma alteração). */
export function applyContentSnapshot(snapshot: SiteContentState): void {
  previousState = state;
  state = snapshot;
  persist();
  emit();
}

/* ------------------------------------------------------------------ */
/* Acesso fora de componentes (libs)                                   */
/* ------------------------------------------------------------------ */

export const getContent = (): SiteContentState => state;
export const getProducts = (): Product[] => state.products;
export const getCategories = (): Category[] => state.categories;
export const getHeroCampaigns = (): HeroCampaign[] => state.heroCampaigns;
export const getDeliveryMethods = (): DeliveryMethodConfig[] => state.delivery;
export const getFreeShippingRules = (): FreeShippingRule[] => state.freeShipping;
export const getWhatsApp = (): WhatsAppContent => state.whatsapp;
export const getStoreSettings = (): StoreSettingsContent => state.settings;
export const getSeo = (): SeoContent => state.seo;

export const getProductById = (id: string): Product | undefined =>
  state.products.find((product) => product.id === id);

export const getCategoryById = (id: string): Category | undefined =>
  state.categories.find((category) => category.id === id);

export const getDeliveryMethodById = (id: string): DeliveryMethodConfig | undefined =>
  state.delivery.find((method) => method.id === id);

/* ------------------------------------------------------------------ */
/* Textos editáveis (registro de campos)                               */
/* ------------------------------------------------------------------ */

export interface TextFieldDef {
  key: string;
  label: string;
  group: string;
  value: string;
  multiline?: boolean;
}

export const TEXT_FIELDS: TextFieldDef[] = [
  // Benefícios
  { key: "benefits.0.title", label: "Destaque 1 — título", group: "Benefícios", value: "Atendimento próximo" },
  { key: "benefits.0.text", label: "Destaque 1 — texto", group: "Benefícios", value: "Cada dúvida é respondida com atenção e cuidado, do primeiro contato à entrega.", multiline: true },
  { key: "benefits.1.title", label: "Destaque 2 — título", group: "Benefícios", value: "Compra facilitada pelo WhatsApp" },
  { key: "benefits.1.text", label: "Destaque 2 — texto", group: "Benefícios", value: "Escolha seus favoritos com tranquilidade e finalize tudo em uma conversa simples.", multiline: true },
  { key: "benefits.2.title", label: "Destaque 3 — título", group: "Benefícios", value: "Entrega e retirada combinadas" },
  { key: "benefits.2.text", label: "Destaque 3 — texto", group: "Benefícios", value: "Você escolhe o melhor jeito de receber, combinado direto no atendimento.", multiline: true },
  { key: "benefits.3.title", label: "Destaque 4 — título", group: "Benefícios", value: "Ajuda para escolher modelo e numeração" },
  { key: "benefits.3.text", label: "Destaque 4 — texto", group: "Benefícios", value: "Contamos com você para acertar o modelo e a numeração certa desde a primeira vez.", multiline: true },

  // Categorias (home)
  { key: "categories.eyebrow", label: "Sobretítulo", group: "Categorias" , value: "Categorias em destaque" },
  { key: "categories.title", label: "Título", group: "Categorias", value: "Encontre o par e os detalhes para o seu estilo" },
  { key: "categories.text", label: "Texto", group: "Categorias", value: "Comece pelos calçados — sandálias, scarpins, sapatilhas, mules, mocassins, tênis, rasteiras, papetes e botas. Depois complete com bolsas, cintos e semijoias.", multiline: true },

  // Seleção
  { key: "selection.eyebrow", label: "Sobretítulo", group: "Seleção Auréa", value: "Vitrine" },
  { key: "selection.title", label: "Título", group: "Seleção Auréa", value: "Seleção Auréa" },
  { key: "selection.text", label: "Texto", group: "Seleção Auréa", value: "Uma amostra da coleção, com calçados de vários tipos e acessórios para completar o look.", multiline: true },

  // Banner editorial
  { key: "editorial.eyebrow", label: "Selo", group: "Banner editorial", value: "Editorial Auréa" },
  { key: "editorial.title", label: "Título", group: "Banner editorial", value: "Elegância que começa nos seus passos" },
  { key: "editorial.text", label: "Subtítulo", group: "Banner editorial", value: "Modelos versáteis, texturas marcantes e acessórios que completam produções para diferentes momentos da vida real.", multiline: true },
  { key: "editorial.button", label: "Texto do botão", group: "Banner editorial", value: "Descobrir a coleção" },

  // Momentos
  { key: "looks.eyebrow", label: "Sobretítulo", group: "Momentos", value: "Momentos" },
  { key: "looks.title", label: "Título", group: "Momentos", value: "Um par para cada momento" },
  { key: "looks.text", label: "Texto", group: "Momentos", value: "Do escritório ao fim de semana, escolhas que acompanham a sua rotina.", multiline: true },

  // Novidades
  { key: "newArrivals.eyebrow", label: "Sobretítulo", group: "Novidades", value: "Chegou agora" },
  { key: "newArrivals.title", label: "Título", group: "Novidades", value: "Acabaram de chegar" },
  { key: "newArrivals.text", label: "Texto", group: "Novidades", value: "Novos modelos, novas cores e mais motivos para renovar os seus passos.", multiline: true },

  // Atendimento
  { key: "support.eyebrow", label: "Selo", group: "Atendimento", value: "Atendimento Auréa" },
  { key: "support.title", label: "Título", group: "Atendimento", value: "Precisa de ajuda para escolher?" },
  { key: "support.text", label: "Texto", group: "Atendimento", value: "Conte para a gente o que você procura. Ajudamos a encontrar o modelo, a numeração e os acessórios que combinam com o seu momento.", multiline: true },
  { key: "support.button", label: "Texto do botão", group: "Atendimento", value: "Falar pelo WhatsApp" },

  // História
  { key: "brandStory.eyebrow", label: "Selo", group: "História da marca", value: "Nossa essência" },
  { key: "brandStory.title", label: "Título", group: "História da marca", value: "Estilo, conforto e personalidade em cada escolha" },

  // Inspirações
  { key: "inspirations.title", label: "Título", group: "Inspirações", value: "Inspirações Auréa" },

  // Vellora
  { key: "vellora.badge", label: "Selo", group: "Bloco Vellora", value: "Projeto demonstrativo" },
  { key: "vellora.title", label: "Título", group: "Bloco Vellora", value: "Gostou desta experiência para o seu negócio?" },
  { key: "vellora.text", label: "Texto", group: "Bloco Vellora", value: "Este é um modelo demonstrativo de loja de calçados e acessórios criado pela Vellora Studio.", multiline: true },
  { key: "vellora.button1", label: "Botão principal", group: "Bloco Vellora", value: "Quero uma loja para minha marca" },
  { key: "vellora.button2", label: "Botão do painel", group: "Bloco Vellora", value: "Conhecer painel de edição" },
  { key: "vellora.button3", label: "Botão secundário", group: "Bloco Vellora", value: "Conhecer a Vellora Studio" },

  // Rodapé
  { key: "footer.signature", label: "Assinatura da marca", group: "Rodapé", value: "Passos que acompanham cada versão de você." },
  { key: "footer.note", label: "Aviso institucional", group: "Rodapé", value: "Modelo demonstrativo criado pela Vellora Studio.", multiline: true },
];

export const getText = (key: string, fallback: string): string => {
  const override = state.texts[key];
  if (override !== undefined && override !== "") return override;
  const field = TEXT_FIELDS.find((item) => item.key === key);
  return field ? field.value : fallback;
};

export const getImage = (key: string, fallback: string): string => state.images[key]?.url ?? fallback;
export const getImageAlt = (key: string, fallback: string): string => state.images[key]?.alt ?? fallback;
export const getImageFocal = (key: string, fallback: string): string =>
  state.images[key]?.focal ?? fallback;

/* ------------------------------------------------------------------ */
/* Mutadores                                                           */
/* ------------------------------------------------------------------ */

export const setTopBar = (messages: string[]): void => commit({ topBarMessages: messages });
export const setHeroCampaigns = (campaigns: HeroCampaign[]): void => commit({ heroCampaigns: campaigns });
export const setTexts = (texts: Record<string, string>): void => commit({ texts: { ...state.texts, ...texts } });
export const setImage = (key: string, override: ImageOverride): void =>
  commit({ images: { ...state.images, [key]: override } });
export const clearImage = (key: string): void => {
  const next = { ...state.images };
  delete next[key];
  commit({ images: next });
};

export const setWhatsApp = (patch: Partial<WhatsAppContent>): void =>
  commit({ whatsapp: { ...state.whatsapp, ...patch } });
export const setSettings = (patch: Partial<StoreSettingsContent>): void =>
  commit({ settings: { ...state.settings, ...patch } });
export const setSeo = (patch: Partial<SeoContent>): void => commit({ seo: { ...state.seo, ...patch } });

/* --------------------------- Produtos ------------------------------ */

const slugify = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48);

const CATEGORY_PREFIX: Record<string, string> = {
  sandalias: "SAN",
  scarpins: "SCA",
  sapatilhas: "SAP",
  mules: "MUL",
  mocassins: "MOC",
  tenis: "TEN",
  rasteiras: "RAS",
  papetes: "PAP",
  botas: "BOT",
  bolsas: "BOL",
  cintos: "CIN",
  semijoias: "SEM",
};

export const nextProductCode = (categoryId: string): string => {
  const prefix = CATEGORY_PREFIX[categoryId] ?? "PRO";
  const used = new Set(state.products.map((product) => product.code));
  let index = 1;
  let code = `AUR-${prefix}-${String(index).padStart(3, "0")}`;
  while (used.has(code)) {
    index += 1;
    code = `AUR-${prefix}-${String(index).padStart(3, "0")}`;
  }
  return code;
};

export const uniqueProductId = (base: string): string => {
  const root = slugify(base) || "produto";
  const used = new Set(state.products.map((product) => product.id));
  if (!used.has(root)) return root;
  let index = 2;
  let candidate = `${root}-${index}`;
  while (used.has(candidate)) {
    index += 1;
    candidate = `${root}-${index}`;
  }
  return candidate;
};

export const upsertProduct = (product: Product): void => {
  const exists = state.products.some((item) => item.id === product.id);
  const products = exists
    ? state.products.map((item) => (item.id === product.id ? product : item))
    : [...state.products, product];
  commit({ products });
};

export const patchProduct = (id: string, patch: Partial<Product>): void => {
  commit({ products: state.products.map((item) => (item.id === id ? { ...item, ...patch } : item)) });
};

export const duplicateProduct = (id: string): Product | undefined => {
  const original = state.products.find((item) => item.id === id);
  if (!original) return undefined;
  const copy: Product = {
    ...clone(original),
    id: uniqueProductId(`${original.name} copia`),
    code: nextProductCode(original.categoryId),
    name: `${original.name} (cópia)`,
  };
  commit({ products: [...state.products, copy] });
  return copy;
};

export const removeProduct = (id: string): void => {
  commit({ products: state.products.filter((item) => item.id !== id) });
};

export const moveProduct = (id: string, direction: -1 | 1): void => {
  const list = [...state.products];
  const index = list.findIndex((item) => item.id === id);
  const target = index + direction;
  if (index < 0 || target < 0 || target >= list.length) return;
  const [item] = list.splice(index, 1);
  list.splice(target, 0, item);
  commit({ products: list });
};

/* -------------------------- Categorias ----------------------------- */

export const upsertCategory = (category: Category): void => {
  const exists = state.categories.some((item) => item.id === category.id);
  const categories = exists
    ? state.categories.map((item) => (item.id === category.id ? category : item))
    : [...state.categories, category];
  commit({ categories });
};

export const moveCategory = (id: string, direction: -1 | 1): void => {
  const list = [...state.categories];
  const index = list.findIndex((item) => item.id === id);
  const target = index + direction;
  if (index < 0 || target < 0 || target >= list.length) return;
  const [item] = list.splice(index, 1);
  list.splice(target, 0, item);
  commit({ categories: list });
};

export const toggleCategoryVisible = (id: string): void => {
  commit({
    categories: state.categories.map((item) =>
      item.id === id ? { ...item, visible: item.visible === false } : item,
    ),
  });
};

/* --------------------------- Entregas ------------------------------ */

export const upsertDelivery = (method: DeliveryMethodConfig): void => {
  const exists = state.delivery.some((item) => item.id === method.id);
  const delivery = exists
    ? state.delivery.map((item) => (item.id === method.id ? method : item))
    : [...state.delivery, method];
  commit({ delivery });
};

export const toggleDelivery = (id: string): void => {
  commit({
    delivery: state.delivery.map((item) =>
      item.id === id ? { ...item, available: !item.available } : item,
    ),
  });
};

export const upsertFreeShipping = (rule: FreeShippingRule): void => {
  const exists = state.freeShipping.some((item) => item.id === rule.id);
  const freeShipping = exists
    ? state.freeShipping.map((item) => (item.id === rule.id ? rule : item))
    : [...state.freeShipping, rule];
  commit({ freeShipping });
};

export const toggleFreeShipping = (id: string): void => {
  commit({
    freeShipping: state.freeShipping.map((item) =>
      item.id === id ? { ...item, enabled: !item.enabled } : item,
    ),
  });
};

/* ---------------------------- Reset -------------------------------- */

export const resetContent = (): void => {
  state = buildDefaults();
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* armazenamento indisponível */
  }
  emit();
};

export const hasContentEdits = (): boolean => {
  try {
    return localStorage.getItem(STORAGE_KEY) !== null;
  } catch {
    return false;
  }
};

/* ------------------------------------------------------------------ */
/* Hooks                                                               */
/* ------------------------------------------------------------------ */

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useSiteContent(): SiteContentState {
  return useSyncExternalStore(subscribe, getContent, getContent);
}

export function useProducts(): Product[] {
  return useSiteContent().products;
}

export function useCategoryList(): Category[] {
  return useSiteContent().categories;
}

export function useHeroCampaigns(): HeroCampaign[] {
  return useSiteContent().heroCampaigns;
}

export function useDeliveryMethods(): DeliveryMethodConfig[] {
  return useSiteContent().delivery;
}

export function useSiteText(key: string, fallback: string): string {
  const content = useSiteContent();
  const override = content.texts[key];
  if (override !== undefined && override !== "") return override;
  const field = TEXT_FIELDS.find((item) => item.key === key);
  return field ? field.value : fallback;
}

/** Produto que deve aparecer no site (não arquivado e ativo). */
export const isLiveProduct = (product: Product): boolean =>
  !product.archived && product.active !== false;

export function useLiveProducts(): Product[] {
  return useSiteContent().products.filter(isLiveProduct);
}

/** Categorias visíveis no site (a exclusão "novidades" fica a cargo do consumidor). */
export const isVisibleCategory = (category: Category): boolean => category.visible !== false;

export function useSiteImage(key: string, fallback: string): string {
  return useSiteContent().images[key]?.url ?? fallback;
}

export function useSiteImageAlt(key: string, fallback: string): string {
  return useSiteContent().images[key]?.alt ?? fallback;
}

export function useSiteImageFocal(key: string, fallback: string): string {
  return useSiteContent().images[key]?.focal ?? fallback;
}